import React, { useState, useEffect } from 'react';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  MenuItem,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ClasificacionMaquinas = () => {
  const [tipos, setTipos] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    tipoMaquina: '',
    subtipoMaquina: '',
    potencia: '',
    proceso: '',
    machineNumber: ''
  });

  // Cargar datos
  const fetchTipos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch('/api/tipos-maquina', { headers });
      
      if (!response.ok) {
        if (response.status === 401) {
          setError('Debes iniciar sesión para ver los tipos de máquina');
        } else if (response.status === 403) {
          setError('No tienes permisos para acceder a esta información');
        } else {
          setError(`Error ${response.status}: ${response.statusText}`);
        }
        return;
      }
      
      const data = await response.json();
      setTipos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error al cargar tipos:', err);
      setError('Error al cargar los tipos de máquina: ' + err.message);
    }
  };

  const fetchMaquinas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch('/api/machines', { headers });
      
      if (response.ok) {
        const data = await response.json();
        setMaquinas(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al cargar máquinas:', err);
    }
  };

  useEffect(() => {
    fetchTipos();
    fetchMaquinas();
  }, []);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const resetForm = () => {
    setForm({
      tipoMaquina: '',
      subtipoMaquina: '',
      potencia: '',
      proceso: '',
      machineNumber: ''
    });
    setEditingId(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setError(null);
    setSuccess(null);
    setOpen(true);
  };

  const handleEdit = (tipo) => {
    setForm({
      tipoMaquina: tipo.tipoMaquina || '',
      subtipoMaquina: tipo.subtipoMaquina || '',
      potencia: tipo.potencia || '',
      proceso: tipo.proceso || '',
      machineNumber: tipo.machineNumber || ''
    });
    setEditingId(tipo._id);
    setError(null);
    setSuccess(null);
    setOpen(true);
  };

  const handleDuplicate = (tipo) => {
    // Cargar todos los datos excepto el numeroMaquina que debe ser único
    setForm({
      tipoMaquina: tipo.tipoMaquina || '',
      subtipoMaquina: tipo.subtipoMaquina || '',
      potencia: tipo.potencia || '',
      proceso: tipo.proceso || '',
      machineNumber: '' // Dejar vacío para que el usuario seleccione uno nuevo
    });
    setEditingId(null); // No es edición, es creación
    setError(null);
    setSuccess(null);
    setOpen(true);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // Validar campos obligatorios
    if (!form.tipoMaquina) {
      alert('El tipo de máquina es obligatorio');
      return;
    }
    if (!form.subtipoMaquina) {
      alert('El subtipo de máquina es obligatorio');
      return;
    }
    if (!form.potencia) {
      alert('La potencia es obligatoria');
      return;
    }
    if (!form.proceso) {
      alert('El proceso es obligatorio');
      return;
    }
    if (!form.machineNumber) {
      alert('El número de máquina es obligatorio');
      return;
    }

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    // Convertir a mayúsculas tipo y subtipo
    const dataToSend = {
      ...form,
      tipoMaquina: form.tipoMaquina.toUpperCase(),
      subtipoMaquina: form.subtipoMaquina.toUpperCase()
    };

    try {
      const url = editingId
        ? `/api/tipos-maquina/${editingId}`
        : '/api/tipos-maquina';
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        setSuccess(editingId ? 'Tipo actualizado correctamente' : 'Tipo creado correctamente');
        setTimeout(() => setSuccess(null), 3000);
        setOpen(false);
        resetForm();
        fetchTipos();
      } else {
        if (response.status === 400) {
          alert('No puede clasificar una máquina que ya está listada');
        } else {
          alert('Error de conexión al servidor');
        }
      }
    } catch (err) {
      alert('Error de conexión al servidor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta clasificación?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`/api/tipos-maquina/${id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSuccess('Clasificación eliminada correctamente');
        setTimeout(() => setSuccess(null), 3000);
        fetchTipos();
      } else {
        const errorData = await response.json();
        alert(`Error al eliminar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      alert('Error de conexión al servidor');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>
          Clasificación de Máquinas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenNew}
          sx={{
            backgroundColor: '#4caf50',
            color: '#080808ff',
            fontFamily: 'Roboto',
            fontWeight: 300,
            '&:hover': { backgroundColor: '#45a049' }
          }}
        >
          Nuevo Tipo
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontFamily: 'Roboto', fontWeight: 200 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, fontFamily: 'Roboto', fontWeight: 200 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tabla de tipos */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#2a2a2a' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1a1a1a' }}>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Número de Máquina</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Tipo de Máquina</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Subtipo</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Potencia</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>Proceso</TableCell>
              <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: '#aaa', fontFamily: 'Roboto', fontWeight: 200, py: 3 }}>
                  No hay clasificaciones registradas
                </TableCell>
              </TableRow>
            ) : (
              tipos.map((tipo) => (
                <TableRow key={tipo._id} sx={{ '&:hover': { backgroundColor: '#3a3a3a' } }}>
                  <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{tipo.machineNumber}</TableCell>
                  <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{tipo.tipoMaquina}</TableCell>
                  <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{tipo.subtipoMaquina}</TableCell>
                  <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{tipo.potencia}</TableCell>
                  <TableCell sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}>{tipo.proceso}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Duplicar">
                      <IconButton onClick={() => handleDuplicate(tipo)} sx={{ color: '#ff9800' }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton onClick={() => handleEdit(tipo)} sx={{ color: '#2196f3' }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton onClick={() => handleDelete(tipo._id)} sx={{ color: '#f44336' }}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: '#fff', fontFamily: 'Roboto', fontWeight: 300 }}>
          {editingId ? 'Editar Clasificación' : 'Nueva Clasificación'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#2a2a2a', pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Número de Máquina *"
                value={form.machineNumber}
                onChange={(e) => handleChange('machineNumber', e.target.value)}
                fullWidth
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                }}
              >
                {maquinas.map((maquina) => (
                  <MenuItem key={maquina._id} value={maquina.machineNumber}>
                    {maquina.machineNumber} - {maquina.machine}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tipo de Máquina *"
                value={form.tipoMaquina}
                onChange={(e) => handleChange('tipoMaquina', e.target.value)}
                fullWidth
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subtipo de Máquina *"
                value={form.subtipoMaquina}
                onChange={(e) => handleChange('subtipoMaquina', e.target.value)}
                fullWidth
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Potencia *"
                value={form.potencia}
                onChange={(e) => handleChange('potencia', e.target.value)}
                fullWidth
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Proceso *"
                value={form.proceso}
                onChange={(e) => handleChange('proceso', e.target.value)}
                fullWidth
                required
                sx={{
                  '& .MuiInputLabel-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiInputBase-root': { color: '#fff', fontFamily: 'Roboto', fontWeight: 200 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#2a2a2a', p: 2 }}>
          <Button 
            onClick={() => setOpen(false)}
            sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 200 }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: '#4caf50',
              color: '#080808ff',
              fontFamily: 'Roboto',
              fontWeight: 300,
              '&:hover': { backgroundColor: '#45a049' }
            }}
          >
            {editingId ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClasificacionMaquinas;
